import { useState, useMemo } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import TechBackground3D from '@/components/TechBackground3D';
import { insightsData } from '@/lib/data';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Calendar, Eye, Share2, Search, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { FaLinkedinIn, FaXTwitter, FaFacebookF, FaLink, FaWhatsapp, FaInstagram } from 'react-icons/fa6';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

const ITEMS_PER_PAGE = 6;

const InsightsPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);

  const categories = ['All', ...Array.from(new Set(insightsData.map(item => item.category)))];

  const handleShare = (insight: any, platform: string) => {
    const url = encodeURIComponent(window.location.origin + '/insights');
    const title = encodeURIComponent(`Check out this insight on Yomi Akande's portfolio: ${insight.title}`);

    switch (platform) {
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${title}&url=${url}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
        break;
      case 'whatsapp':
        window.open(`https://api.whatsapp.com/send?text=${title}%20${url}`, '_blank');
        break;
      case 'instagram':
        navigator.clipboard.writeText(`${decodeURIComponent(title)}\n\n${decodeURIComponent(url)}`);
        alert("Title and Link copied to clipboard! Open Instagram to share.");
        window.open('https://instagram.com', '_blank');
        break;
      case 'copy':
        navigator.clipboard.writeText(decodeURIComponent(url));
        alert("Article link copied to clipboard!");
        break;
    }
  };

  const filteredInsights = useMemo(() => {
    return insightsData.filter((item) => {
      const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'All' || item.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, selectedCategory]);

  const totalPages = Math.ceil(filteredInsights.length / ITEMS_PER_PAGE);
  const paginatedInsights = filteredInsights.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  return (
    <div className='min-h-screen bg-background relative flex flex-col'>
      <TechBackground3D />

      <div className='relative z-10 flex flex-col flex-1 pb-20'>
        <Navigation />

        <main className='mt-[80px] lg:mt-[100px] flex-1 container mx-auto px-4 lg:px-8'>

          <div className='mb-12'>
            <Badge variant="outline" className="mb-4">Knowledge Base</Badge>
            <h1 className='text-4xl lg:text-6xl font-bold mb-6 tracking-tight'>
              Engineering <span className='text-primary italic'>Insights</span>
            </h1>
            <p className='text-xl text-muted-foreground max-w-2xl'>
              Discover deep-dives on FinTech architecture, system reliability, and scaling engineering teams.
            </p>
          </div>

          <div className='flex flex-col md:flex-row gap-6 justify-between items-center mb-12 bg-card/40 backdrop-blur-md border border-border/50 p-6 rounded-2xl shadow-xl'>
            <div className="relative w-full max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search articles..."
                className="pl-10 h-12 bg-background border-primary/20 focus-visible:ring-primary"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <div className="flex gap-2 flex-wrap justify-center md:justify-end">
              {categories.map((cat) => (
                <Badge
                  key={cat}
                  variant={selectedCategory === cat ? 'default' : 'outline'}
                  className={`cursor-pointer px-4 py-2 transition-colors ${selectedCategory === cat ? 'bg-primary text-primary-foreground' : 'bg-background/50 text-foreground border-border/50 hover:bg-primary/20'}`}
                  onClick={() => {
                    setSelectedCategory(cat);
                    setCurrentPage(1);
                  }}
                >
                  {cat}
                </Badge>
              ))}
            </div>
          </div>

          {paginatedInsights.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {paginatedInsights.map((insight, index) => (
                <Card
                  key={insight.id}
                  className="card-elegant overflow-hidden flex flex-col group hover:glow-effect transition-all duration-500 cursor-pointer animate-fade-up border-primary/10 bg-card/60 backdrop-blur-sm"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="relative h-48 sm:h-56 overflow-hidden border-b border-border/50">
                    <img
                      src={insight.imageUrl}
                      alt={insight.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute top-4 left-4">
                      <Badge variant="secondary" className="backdrop-blur-xl bg-background/90 text-foreground shadow-lg border-primary/20">
                        {insight.category}
                      </Badge>
                    </div>
                  </div>
                  <CardHeader className="pb-4 pt-6">
                    <div className="flex items-center text-xs text-muted-foreground mb-3">
                      <Calendar className="w-4 h-4 mr-1.5 text-primary/70" />
                      {insight.date}
                    </div>
                    <CardTitle className="text-xl group-hover:text-primary transition-colors duration-300 line-clamp-2">
                      {insight.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 flex-1 flex flex-col justify-between">
                    <p className="text-muted-foreground leading-relaxed line-clamp-3">
                      {insight.excerpt}
                    </p>

                    <div className="flex items-center justify-between pt-4 border-t border-border/50">
                      <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                        <div className="flex items-center">
                          <Eye className="w-3 h-3 mr-1" />
                          {insight.views}
                        </div>
                        <span>{insight.readTime}</span>
                      </div>

                      <div className="flex items-center space-x-2">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <Share2 className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48 bg-card/95 backdrop-blur-md border-border/50">
                            <DropdownMenuItem onClick={() => handleShare(insight, 'twitter')} className="cursor-pointer gap-2">
                              <FaXTwitter className="w-4 h-4 text-muted-foreground" /> <span>X (Twitter)</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleShare(insight, 'linkedin')} className="cursor-pointer gap-2">
                              <FaLinkedinIn className="w-4 h-4 text-muted-foreground" /> <span>LinkedIn</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleShare(insight, 'facebook')} className="cursor-pointer gap-2">
                              <FaFacebookF className="w-4 h-4 text-muted-foreground" /> <span>Facebook</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleShare(insight, 'whatsapp')} className="cursor-pointer gap-2">
                              <FaWhatsapp className="w-4 h-4 text-muted-foreground" /> <span>WhatsApp</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleShare(insight, 'instagram')} className="cursor-pointer gap-2">
                              <FaInstagram className="w-4 h-4 text-muted-foreground" /> <span>Instagram</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleShare(insight, 'copy')} className="cursor-pointer gap-2">
                              <FaLink className="w-4 h-4 text-muted-foreground" /> <span>Copy Link</span>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <Button variant="ghost" size="sm" className="group/btn text-xs hover:bg-primary/10">
                          Read
                          <ArrowRight className="ml-1 w-3 h-3 group-hover/btn:translate-x-1 transition-transform" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-24 text-center border border-dashed border-border/50 rounded-2xl bg-card/20">
              <Search className="w-12 h-12 text-muted-foreground/30 mb-4" />
              <h3 className="text-xl font-bold mb-2">No insights found</h3>
              <p className="text-muted-foreground">Adjust your search parameters to find articles.</p>
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-16 animate-fade-in">
              <Button
                variant="outline" size="icon" disabled={currentPage === 1}
                onClick={() => { setCurrentPage(prev => Math.max(prev - 1, 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="w-10 h-10 border-primary/20 hover:bg-primary/10"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <span className="text-sm font-medium font-mono text-muted-foreground">
                Page <span className="text-foreground">{currentPage}</span> of {totalPages}
              </span>
              <Button
                variant="outline" size="icon" disabled={currentPage === totalPages}
                onClick={() => { setCurrentPage(prev => Math.min(prev + 1, totalPages)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="w-10 h-10 border-primary/20 hover:bg-primary/10"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          )}

          {/* Newsletter Subscription */}
          <div className="mt-24 mb-12">
            <Card className="card-elegant max-w-3xl mx-auto p-8 tech-gradient text-white">
              <div className="text-center">
                <h3 className="text-2xl lg:text-3xl font-bold mb-4">
                  Stay Updated with Tech Insights
                </h3>
                <p className="text-lg opacity-90 mb-6">
                  Subscribe to receive the latest articles, industry insights, and technology trends
                  directly in your inbox. No spam, just valuable content.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                  <div className="flex-1">
                    <input
                      type="email"
                      placeholder="Enter your email"
                      className="w-full px-4 py-3 rounded-lg text-foreground bg-white border-0 focus:ring-2 focus:ring-white/20"
                    />
                  </div>
                  <Button size="lg" variant="secondary" className="bg-primary text-primary-foreground border-border/50 hover:bg-primary/90">
                    Subscribe
                  </Button>
                </div>
                <p className="text-xs opacity-75 mt-4">
                  Join 5,000+ technology leaders who trust our insights
                </p>
              </div>
            </Card>
          </div>
        </main>
        
        <Footer />
      </div>
    </div>
  );
};

export default InsightsPage;
